/**
 * Ranger — native RV Match A.I. customer assistant chat widget.
 *
 * Replaces the previous third-party <script> injection. Renders a floating
 * chat bubble bottom-right on public marketing pages and a cinematic dark
 * chat drawer that talks to /api/ranger/session/init + /api/ranger/chat.
 *
 * Suppressed on /embed/, /dealer, /admin, /intake-form, /dealer-auth (same
 * rules as the old inline guard in index.html).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = '/icons/ranger-avatar.jpg';

const SUPPRESS_PREFIXES = [
  '/embed/',
  '/dealer',
  '/admin',
  '/intake-form',
  '/dealer-auth',
  '/rigready',
];

const RangerWidget = () => {
  const location = useLocation();
  const shouldSuppress = useMemo(
    () => SUPPRESS_PREFIXES.some((p) => location.pathname.startsWith(p)),
    [location.pathname],
  );

  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // {role: 'assistant'|'user', content}
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [initError, setInitError] = useState(false);
  const [showCaptureForm, setShowCaptureForm] = useState(false);
  const [captured, setCaptured] = useState(false);
  const [captureBusy, setCaptureBusy] = useState(false);
  const [captureError, setCaptureError] = useState('');
  const [captureName, setCaptureName] = useState('');
  const [captureEmail, setCaptureEmail] = useState('');
  const [capturePhone, setCapturePhone] = useState('');
  const [captureZip, setCaptureZip] = useState('');
  const [showTowPanel, setShowTowPanel] = useState(false);
  const [towVin, setTowVin] = useState('');
  const [towBusy, setTowBusy] = useState(false);
  const [towError, setTowError] = useState('');
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const resetSession = () => {
    setSessionId(null);
    setMessages([]);
    setInput('');
    setShowCaptureForm(false);
    setCaptured(false);
    setCaptureError('');
    setCaptureName('');
    setCaptureEmail('');
    setCapturePhone('');
    setCaptureZip('');
    setInitError(false);
  };

  // Initialize session lazily the first time the drawer opens
  useEffect(() => {
    if (!open || sessionId || shouldSuppress) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/api/ranger/session/init`, {
          referrer: window.location.href,
        });
        if (cancelled) return;
        setSessionId(data.session_id);
        setMessages([{ role: 'assistant', content: data.greeting }]);
      } catch (e) {
        setInitError(true);
        setMessages([{
          role: 'assistant',
          content: "I couldn't wake up just yet. Please close and reopen the chat in a moment.",
        }]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, sessionId, shouldSuppress]);

  // Autoscroll on new messages
  useEffect(() => {
    if (!open || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 260);
      return () => clearTimeout(t);
    }
  }, [open]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending || !sessionId) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/api/ranger/chat`, {
        session_id: sessionId,
        message: text,
      });
      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply }]);
      // Backend flagged this as a good moment to capture the buyer
      if (data.capture_prompt && !captured) {
        setShowCaptureForm(true);
        setMessages((prev) => [...prev, {
          role: 'assistant',
          isCaptureInvite: true,
          content: "Want me to save this chat so a real RV Match advisor can follow up? Please fill in every field below — takes 10 seconds.",
        }]);
      }
    } catch (err) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: "Couldn't reach Ranger right now. Try once more?",
      }]);
    } finally {
      setSending(false);
    }
  };

  const submitCapture = async (e) => {
    e?.preventDefault?.();
    if (captureBusy || !sessionId) return;
    const name = captureName.trim();
    const email = captureEmail.trim();
    const phone = capturePhone.trim();
    const zip = captureZip.trim();
    if (!name || !email || !phone || !zip) {
      setCaptureError('Please complete every field.');
      return;
    }
    setCaptureBusy(true);
    setCaptureError('');
    try {
      const { data } = await axios.post(`${API}/api/ranger/lead-capture`, {
        session_id: sessionId,
        name,
        email,
        phone,
        zip_code: zip,
        consent: true,
      });
      setCaptured(true);
      setShowCaptureForm(false);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: data.confirmation || "Thanks — I've saved this chat. An advisor will be in touch soon.",
      }]);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not save right now — try again in a moment.';
      setCaptureError(typeof msg === 'string' ? msg : 'Could not save right now — try again.');
    } finally {
      setCaptureBusy(false);
    }
  };

  const runTowLookup = async ({ vin, image_base64 }) => {
    if (!sessionId) return;
    setTowBusy(true);
    setTowError('');
    try {
      const { data } = await axios.post(`${API}/api/ranger/tow-lookup`, {
        session_id: sessionId,
        vin: vin || undefined,
        image_base64: image_base64 || undefined,
      });
      const yearMakeModel = [data.year, data.make, data.model, data.trim].filter(Boolean).join(' ');
      const capacity = data.tow_capacity_lbs
        ? `${data.tow_capacity_lbs.toLocaleString()} lbs`
        : (data.tow_capacity_range || 'not published');
      const summary =
        `Here's what I found for the ${yearMakeModel}:\n\n` +
        `• Tow capacity: ${capacity}` +
        (data.payload_lbs ? `\n• Payload: ${data.payload_lbs.toLocaleString()} lbs` : '') +
        (data.confidence ? `\n• Confidence: ${data.confidence}` : '') +
        (data.caveat ? `\n\n${data.caveat}` : '') +
        (data.advice ? `\n\n${data.advice}` : '');
      setMessages((prev) => [...prev, { role: 'assistant', content: summary }]);
      setShowTowPanel(false);
      setTowVin('');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not look that up. Double-check the VIN or try a clearer photo.';
      setTowError(typeof msg === 'string' ? msg : 'Lookup failed. Try again.');
    } finally {
      setTowBusy(false);
    }
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChosen = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4_000_000) {
      setTowError('Image is too large. Try a photo under 4 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result || '').split(',')[1] || '';
      runTowLookup({ image_base64: b64 });
    };
    reader.readAsDataURL(f);
    e.target.value = '';
  };

  if (shouldSuppress) return null;

  return (
    <>
      {/* Bubble */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="ranger-bubble-btn"
          aria-label="Open Ranger chat"
          className="rangerBubble"
        >
          <img src={LOGO} alt="Ranger" className="rangerBubbleImg" />
          <span className="rangerBubbleDot" />
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div className="rangerDrawer" data-testid="ranger-drawer" role="dialog" aria-label="Ranger chat">
          <header className="rangerHeader">
            <div className="rangerHeaderLeft">
              <div className="rangerAvatarWrap">
                <img src={LOGO} alt="Ranger" className="rangerAvatar" />
                <span className="rangerOnlineDot" />
              </div>
              <div>
                <div className="rangerName">Ranger</div>
                <div className="rangerSubtitle">YOUR RV &amp; TRAVEL A.I. GUIDE</div>
              </div>
            </div>
            <div className="rangerHeaderRight">
              {!captured && sessionId && messages.length > 1 && (
                <button
                  type="button"
                  onClick={() => { setShowCaptureForm(true); setCaptureError(''); }}
                  data-testid="ranger-save-chat-btn"
                  className="rangerSaveChip"
                  aria-label="Save chat as lead"
                >
                  Save chat
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="ranger-minimize-btn"
                aria-label="Minimize chat"
                className="rangerCloseBtn"
                title="Minimize"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="6" y1="14" x2="18" y2="14"/></svg>
              </button>
              <button
                type="button"
                onClick={() => { resetSession(); setOpen(false); }}
                data-testid="ranger-close-btn"
                aria-label="Close chat"
                className="rangerCloseBtn"
                title="End chat"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          </header>

          <div ref={scrollRef} className="rangerBody" data-testid="ranger-messages">
            {messages.map((m, i) => (
              <div key={i} className={`rangerRow ${m.role === 'user' ? 'rangerRowUser' : 'rangerRowBot'}`}>
                {m.role === 'assistant' && (
                  <img src={LOGO} alt="" className="rangerRowAvatar" />
                )}
                <div className={`rangerBubbleMsg ${m.role === 'user' ? 'rangerBubbleUser' : 'rangerBubbleBot'} ${m.isCaptureInvite ? 'rangerCaptureInvite' : ''}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {showCaptureForm && !captured && (
              <form
                onSubmit={submitCapture}
                className="rangerCaptureCard"
                data-testid="ranger-capture-form"
              >
                <div className="rangerCaptureTitle">Save this chat</div>
                <input
                  type="text"
                  placeholder="Your name"
                  value={captureName}
                  onChange={(e) => setCaptureName(e.target.value)}
                  className="rangerCaptureInput"
                  data-testid="ranger-capture-name"
                  maxLength={120}
                  autoComplete="name"
                  disabled={captureBusy}
                  required
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={captureEmail}
                  onChange={(e) => setCaptureEmail(e.target.value)}
                  className="rangerCaptureInput"
                  data-testid="ranger-capture-email"
                  maxLength={200}
                  autoComplete="email"
                  disabled={captureBusy}
                  required
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={capturePhone}
                  onChange={(e) => setCapturePhone(e.target.value)}
                  className="rangerCaptureInput"
                  data-testid="ranger-capture-phone"
                  maxLength={40}
                  autoComplete="tel"
                  disabled={captureBusy}
                  required
                />
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="ZIP code"
                  value={captureZip}
                  onChange={(e) => setCaptureZip(e.target.value)}
                  className="rangerCaptureInput"
                  data-testid="ranger-capture-zip"
                  maxLength={20}
                  autoComplete="postal-code"
                  disabled={captureBusy}
                  required
                />
                {captureError && (
                  <div className="rangerCaptureError" data-testid="ranger-capture-error">{captureError}</div>
                )}
                <div className="rangerCaptureRow">
                  <button
                    type="button"
                    onClick={() => { setShowCaptureForm(false); setCaptureError(''); }}
                    className="rangerCaptureSkip"
                    data-testid="ranger-capture-skip"
                    disabled={captureBusy}
                  >
                    Not now
                  </button>
                  <button
                    type="submit"
                    className="rangerCaptureSubmit"
                    data-testid="ranger-capture-submit"
                    disabled={captureBusy}
                  >
                    {captureBusy ? 'Saving…' : 'Save & follow up'}
                  </button>
                </div>
                <div className="rangerCaptureLegal">
                  By submitting, you agree to be contacted by an RV Match advisor.
                </div>
              </form>
            )}
            {sending && (
              <div className="rangerRow rangerRowBot">
                <img src={LOGO} alt="" className="rangerRowAvatar" />
                <div className="rangerBubbleMsg rangerBubbleBot rangerTyping" data-testid="ranger-typing">
                  <span /><span /><span />
                </div>
              </div>
            )}
          </div>

          {/* Tow-capacity quick action */}
          <div className="rangerQuickBar">
            <button
              type="button"
              className={`rangerQuickChip ${showTowPanel ? 'active' : ''}`}
              onClick={() => { setShowTowPanel((v) => !v); setTowError(''); }}
              data-testid="ranger-tow-toggle"
              disabled={!sessionId}
            >
              🚚 Tow Capacity
            </button>
          </div>
          {showTowPanel && (
            <div className="rangerTowPanel" data-testid="ranger-tow-panel">
              <div className="rangerTowTitle">Check your vehicle&apos;s tow capacity</div>
              <div className="rangerTowRow">
                <input
                  type="text"
                  value={towVin}
                  onChange={(e) => setTowVin(e.target.value.toUpperCase())}
                  placeholder="Type your 17-char VIN"
                  className="rangerCaptureInput"
                  data-testid="ranger-tow-vin"
                  maxLength={17}
                  disabled={towBusy}
                />
                <button
                  type="button"
                  onClick={() => runTowLookup({ vin: towVin })}
                  disabled={towBusy || towVin.trim().length !== 17}
                  className="rangerCaptureSubmit"
                  data-testid="ranger-tow-lookup"
                >
                  {towBusy ? '…' : 'Look up'}
                </button>
              </div>
              <div className="rangerTowOr">or</div>
              <button
                type="button"
                onClick={onPickFile}
                disabled={towBusy}
                className="rangerTowUpload"
                data-testid="ranger-tow-upload"
              >
                📷 Upload door-jamb sticker photo
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={onFileChosen}
              />
              {towError && <div className="rangerCaptureError" data-testid="ranger-tow-error">{towError}</div>}
            </div>
          )}

          <form className="rangerFooter" onSubmit={send}>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) send(e); }}
              placeholder="Ask Ranger anything…"
              className="rangerInput"
              data-testid="ranger-input"
              disabled={sending || initError || !sessionId}
              maxLength={2000}
            />
            <button
              type="submit"
              disabled={sending || !input.trim() || !sessionId}
              className="rangerSendBtn"
              data-testid="ranger-send-btn"
              aria-label="Send message"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </button>
          </form>

          <div className="rangerBrand">Powered by Ranger A.I. • RV Match A.I.</div>
        </div>
      )}

      <style>{`
        .rangerBubble {
          position: fixed;
          bottom: 20px;
          right: 20px;
          width: 60px;
          height: 60px;
          border-radius: 999px;
          background: linear-gradient(135deg, #F4A261 0%, #E76F51 100%);
          box-shadow: 0 12px 32px rgba(244, 162, 97, 0.4), 0 4px 12px rgba(0,0,0,0.3);
          border: none;
          cursor: pointer;
          z-index: 2147483000;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        /* On mobile, lift the button above the fixed bottom nav bar so it
           doesn't cover Overview/Pipeline/etc. Also shrink slightly so it's
           less intrusive in the corner. */
        @media (max-width: 768px) {
          .rangerBubble {
            bottom: 84px;
            right: 14px;
            width: 52px;
            height: 52px;
          }
          .rangerBubbleImg { width: 36px; height: 36px; }
        }
        .rangerBubble:hover { transform: scale(1.06); box-shadow: 0 16px 40px rgba(244, 162, 97, 0.55), 0 6px 16px rgba(0,0,0,0.35); }
        .rangerBubble:active { transform: scale(0.96); }
        .rangerBubbleImg { width: 42px; height: 42px; border-radius: 999px; object-fit: cover; }
        .rangerBubbleDot { position: absolute; bottom: 6px; right: 6px; width: 12px; height: 12px; border-radius: 999px; background: #34D399; border: 2px solid #1a1a1a; }

        .rangerDrawer {
          position: fixed;
          bottom: 20px;
          right: 20px;
          width: 380px;
          max-width: calc(100vw - 32px);
          height: 620px;
          max-height: calc(100vh - 32px);
          background: #0f1116;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 20px;
          box-shadow: 0 24px 60px rgba(0,0,0,0.55), 0 4px 16px rgba(0,0,0,0.35);
          z-index: 2147483000;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          animation: rangerRise 0.25s ease-out;
          color: #e8ecf3;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }
        /* On mobile, dock the drawer to the bottom of the screen above the
           mobile nav so it doesn't get hidden behind it. */
        @media (max-width: 768px) {
          .rangerDrawer {
            bottom: 80px;
            right: 8px;
            left: 8px;
            width: auto;
            max-width: none;
            height: calc(100vh - 100px);
            max-height: 80vh;
          }
        }
        @keyframes rangerRise { from { opacity: 0; transform: translateY(12px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }

        .rangerHeader {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 16px;
          background: linear-gradient(180deg, #14181f 0%, #0f1116 100%);
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .rangerHeaderLeft { display: flex; align-items: center; gap: 12px; }
        .rangerHeaderRight { display: flex; align-items: center; gap: 4px; }
        .rangerSaveChip {
          background: rgba(244, 162, 97, 0.14);
          border: 1px solid rgba(244, 162, 97, 0.4);
          color: #F4A261;
          font-size: 11px;
          font-weight: 600;
          padding: 5px 10px;
          border-radius: 999px;
          cursor: pointer;
          margin-right: 4px;
          transition: background 0.15s, color 0.15s;
        }
        .rangerSaveChip:hover { background: rgba(244, 162, 97, 0.24); color: #ffffff; }
        .rangerAvatarWrap { position: relative; }
        .rangerAvatar { width: 40px; height: 40px; border-radius: 999px; object-fit: cover; border: 2px solid rgba(244, 162, 97, 0.5); }
        .rangerOnlineDot { position: absolute; bottom: 0; right: 0; width: 10px; height: 10px; border-radius: 999px; background: #34D399; border: 2px solid #14181f; }
        .rangerName { font-size: 16px; font-weight: 700; line-height: 1.1; color: #ffffff; }
        .rangerSubtitle { font-size: 10px; letter-spacing: 0.08em; color: #F4A261; font-weight: 600; margin-top: 2px; }
        .rangerCloseBtn { background: transparent; border: none; color: #9aa3b2; cursor: pointer; padding: 6px; border-radius: 8px; transition: background 0.15s, color 0.15s; }
        .rangerCloseBtn:hover { background: rgba(255,255,255,0.06); color: #ffffff; }

        .rangerBody {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          background: radial-gradient(circle at 100% 0%, rgba(244, 162, 97, 0.04) 0%, transparent 50%), #0f1116;
        }
        .rangerBody::-webkit-scrollbar { width: 6px; }
        .rangerBody::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }

        .rangerRow { display: flex; align-items: flex-end; gap: 8px; }
        .rangerRowBot { justify-content: flex-start; }
        .rangerRowUser { justify-content: flex-end; }
        .rangerRowAvatar { width: 28px; height: 28px; border-radius: 999px; flex-shrink: 0; object-fit: cover; opacity: 0.9; }

        .rangerBubbleMsg {
          max-width: 78%;
          padding: 10px 14px;
          border-radius: 16px;
          font-size: 14px;
          line-height: 1.5;
          word-wrap: break-word;
          white-space: pre-wrap;
        }
        .rangerBubbleBot { background: rgba(255,255,255,0.05); color: #e8ecf3; border-top-left-radius: 4px; border: 1px solid rgba(255,255,255,0.04); }
        .rangerBubbleUser { background: linear-gradient(135deg, #F4A261 0%, #E76F51 100%); color: #1a1210; border-top-right-radius: 4px; font-weight: 500; }
        .rangerCaptureInvite { border: 1px solid rgba(244, 162, 97, 0.35); background: rgba(244, 162, 97, 0.08); }

        .rangerCaptureCard {
          margin: 4px 0 4px 36px;
          padding: 14px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(244, 162, 97, 0.35);
          border-radius: 14px;
          display: flex; flex-direction: column; gap: 8px;
          animation: rangerRise 0.25s ease-out;
        }
        .rangerCaptureTitle { font-size: 13px; font-weight: 700; color: #ffffff; letter-spacing: 0.02em; margin-bottom: 2px; }
        .rangerCaptureInput {
          height: 36px;
          border-radius: 10px;
          background: rgba(0,0,0,0.35);
          border: 1px solid rgba(255,255,255,0.08);
          color: #ffffff;
          padding: 0 12px;
          font-size: 13px;
          outline: none;
          transition: border-color 0.15s;
        }
        .rangerCaptureInput::placeholder { color: #6b7280; }
        .rangerCaptureInput:focus { border-color: #F4A261; }
        .rangerCaptureInput:disabled { opacity: 0.6; }
        .rangerCaptureError { font-size: 11px; color: #F87171; margin-top: 2px; }
        .rangerCaptureRow { display: flex; gap: 8px; margin-top: 4px; }
        .rangerCaptureSkip {
          flex: 0 0 auto;
          background: transparent;
          border: 1px solid rgba(255,255,255,0.1);
          color: #9aa3b2;
          padding: 8px 14px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.15s, color 0.15s;
        }
        .rangerCaptureSkip:hover { background: rgba(255,255,255,0.06); color: #ffffff; }
        .rangerCaptureSubmit {
          flex: 1;
          background: linear-gradient(135deg, #F4A261 0%, #E76F51 100%);
          border: none;
          color: #1a1210;
          padding: 8px 14px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: transform 0.15s, opacity 0.15s;
        }
        .rangerCaptureSubmit:hover:not(:disabled) { transform: scale(1.02); }
        .rangerCaptureSubmit:disabled { opacity: 0.6; cursor: not-allowed; }
        .rangerCaptureLegal { font-size: 10px; color: #6b7280; margin-top: 4px; line-height: 1.4; }

        .rangerTyping { display: inline-flex; gap: 4px; padding: 12px 14px; }
        .rangerTyping span {
          width: 6px; height: 6px; border-radius: 999px; background: #F4A261;
          animation: rangerBlink 1.2s infinite ease-in-out;
        }
        .rangerTyping span:nth-child(2) { animation-delay: 0.15s; }
        .rangerTyping span:nth-child(3) { animation-delay: 0.3s; }
        @keyframes rangerBlink { 0%, 60%, 100% { opacity: 0.3; transform: translateY(0); } 30% { opacity: 1; transform: translateY(-2px); } }

        .rangerFooter {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 14px 8px;
          background: #0f1116;
          border-top: 1px solid rgba(255,255,255,0.06);
        }
        .rangerQuickBar { padding: 6px 14px 0; display: flex; gap: 6px; flex-wrap: wrap; }
        .rangerQuickChip {
          background: rgba(244, 162, 97, 0.10);
          border: 1px solid rgba(244, 162, 97, 0.35);
          color: #F4A261; font-size: 11px; font-weight: 600;
          padding: 4px 10px; border-radius: 999px; cursor: pointer;
          transition: background 0.15s;
        }
        .rangerQuickChip:hover:not(:disabled) { background: rgba(244, 162, 97, 0.22); color: #ffffff; }
        .rangerQuickChip.active { background: linear-gradient(135deg,#F4A261,#E76F51); color: #1a1210; border-color: transparent; }
        .rangerQuickChip:disabled { opacity: 0.4; cursor: not-allowed; }
        .rangerTowPanel {
          margin: 6px 14px 0;
          padding: 12px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(244, 162, 97, 0.35);
          border-radius: 12px;
          display: flex; flex-direction: column; gap: 8px;
        }
        .rangerTowTitle { font-size: 12px; font-weight: 700; color: #ffffff; }
        .rangerTowRow { display: flex; gap: 6px; }
        .rangerTowOr { text-align: center; font-size: 10px; color: #6b7280; letter-spacing: 0.1em; text-transform: uppercase; }
        .rangerTowUpload {
          padding: 10px;
          border-radius: 10px;
          background: rgba(244, 162, 97, 0.10);
          border: 1px dashed rgba(244, 162, 97, 0.5);
          color: #F4A261; font-size: 12px; font-weight: 600;
          cursor: pointer;
        }
        .rangerTowUpload:hover:not(:disabled) { background: rgba(244, 162, 97, 0.18); color: #ffffff; }
        .rangerTowUpload:disabled { opacity: 0.5; cursor: not-allowed; }
        .rangerInput {
          flex: 1;
          height: 40px;
          border-radius: 999px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(244, 162, 97, 0.35);
          color: #ffffff;
          padding: 0 16px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.15s, background 0.15s;
        }
        .rangerInput::placeholder { color: #6b7280; }
        .rangerInput:focus { border-color: #F4A261; background: rgba(255,255,255,0.06); }
        .rangerInput:disabled { opacity: 0.6; cursor: not-allowed; }

        .rangerSendBtn {
          width: 40px; height: 40px; border-radius: 999px;
          background: linear-gradient(135deg, #F4A261 0%, #E76F51 100%);
          border: none; color: #1a1210; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: transform 0.15s, opacity 0.15s;
        }
        .rangerSendBtn:hover:not(:disabled) { transform: scale(1.05); }
        .rangerSendBtn:disabled { opacity: 0.4; cursor: not-allowed; }
        .rangerSendBtn svg { transform: translateX(1px); }

        .rangerBrand { text-align: center; font-size: 10px; color: #6b7280; padding: 4px 0 10px; letter-spacing: 0.02em; }

        @media (max-width: 480px) {
          .rangerDrawer { right: 8px; left: 8px; bottom: 80px; width: auto; height: calc(100vh - 100px); max-height: 80vh; }
          .rangerBubble { right: 14px; bottom: 84px; }
        }
      `}</style>
    </>
  );
};

export default RangerWidget;
